import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "./GlassPanel";
import { Mail, RefreshCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * Return Nudge panel — surfaces contacts whose most recent archived job is
 * more than N months old and gives the receptionist a one-click "mail with
 * a template" shortcut so it's easy to prompt a re-wrap or detailing visit.
 */
export default function ReturnNudgesPanel() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m = months) => {
    setLoading(true);
    try {
      const { data } = await api.get("/analytics/return-nudges", { params: { months: m } });
      setData(data);
    } catch (err) {
      toast.error("Could not load return-nudge candidates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(months); }, [months]);

  const mailtoFor = (c) => {
    const subject = encodeURIComponent(`Time for a fresh look, ${c.contact_name?.split(" ")[0] || ""}?`);
    const body = encodeURIComponent(
      `Hi ${c.contact_name?.split(" ")[0] || "there"},\n\n` +
      `It's been about ${Math.round(c.days_since / 30)} months since we finished your ${c.last_vehicle || "vehicle"} ` +
      `(${c.last_service || "wrap"}) — we'd love to have you back for a re-wrap, colour refresh, or ceramic top-up.\n\n` +
      `Reply to this email or give us a call and we'll get you on the calendar.\n\n` +
      `— WrapConsole`
    );
    return `mailto:${c.email}?subject=${subject}&body=${body}`;
  };

  const count = data?.count ?? 0;

  return (
    <GlassPanel className="!p-6" data-testid="return-nudges-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-label flex items-center gap-1"><Sparkles className="w-3 h-3" /> Return-nudge queue</div>
          <h3 className="font-heading text-xl text-white mt-1">Bring past customers back</h3>
          <p className="text-xs text-slate-400 mt-1 max-w-md">
            Contacts with no active job whose last archived visit is over{" "}
            <select
              data-testid="return-nudges-months"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="bg-black/40 border border-white/10 rounded-md px-1.5 py-0.5 text-white mx-0.5"
            >
              {[3, 6, 9, 12, 18].map((m) => <option key={m} value={m} className="bg-neutral-900">{m}</option>)}
            </select>{" "}
            months ago.
          </p>
        </div>
        <button
          data-testid="return-nudges-refresh"
          onClick={() => load(months)}
          className="text-xs flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 rounded-full px-3 py-1.5"
        >
          <RefreshCcw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {loading && <div className="text-slate-500 italic text-sm py-2">Loading…</div>}
      {!loading && count === 0 && (
        <div className="text-slate-500 italic text-sm py-6 text-center" data-testid="return-nudges-empty">
          No customers waiting for a nudge. Everyone&apos;s already been in recently.
        </div>
      )}
      {!loading && count > 0 && (
        <div className="space-y-2">
          {data.contacts.map((c) => (
            <div
              key={c.contact_id}
              data-testid={`return-nudge-${c.contact_id}`}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-2xl px-3 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">
                  {c.contact_name}
                  <span className="text-xs text-slate-500 ml-2">· {c.email || c.phone || "no contact info"}</span>
                </div>
                <div className="text-[11px] text-slate-400 truncate">
                  Last: {c.last_vehicle || "—"} — {c.last_service || "—"}
                  <span className="text-slate-500"> · {c.days_since} days ago</span>
                </div>
              </div>
              {c.email ? (
                <a
                  href={mailtoFor(c)}
                  data-testid={`return-nudge-mailto-${c.contact_id}`}
                  className="text-xs flex items-center gap-1 bg-white text-black font-semibold rounded-full px-3 py-1.5 hover:bg-slate-100"
                >
                  <Mail className="w-3 h-3" /> Draft email
                </a>
              ) : (
                <span data-testid={`return-nudge-no-email-${c.contact_id}`} className="text-[10px] text-slate-500 italic">no email on file</span>
              )}
            </div>
          ))}
        </div>
      )}
    </GlassPanel>
  );
}
